import React, { useEffect, useState } from "react";
import { FaCheckCircle, FaCopy, FaDownload, FaExternalLinkAlt, FaPaperPlane } from "react-icons/fa";
import { useNotify } from "../../components/ConfirmModal";
import { Modal, StatusPill } from "./WorkUi";
import { errorMessage, fmtShortDate, fmtTime } from "./workApi";

/**
 * Quick publish: everything needed to post in one place (caption, media, account), then one tap
 * on "Mark posted". With connected accounts the item can also be published automatically.
 */
function ReadyPack({ api, itemId, onClose, onPosted }) {
  const notify = useNotify();
  const [pack, setPack] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [picked, setPicked] = useState([]);
  const [when, setWhen] = useState("now");

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.readyPack(itemId), api.publishJobs(itemId).catch(() => ({ jobs: [] }))])
      .then(([p, j]) => {
        if (cancelled) return;
        setPack(p);
        setUrl(p?.Posted_URL || "");
        setJobs(j?.jobs || []);
      })
      .catch((err) => !cancelled && setError(errorMessage(err, "Could not load the post details")));
    return () => {
      cancelled = true;
    };
  }, [api, itemId]);

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(pack.Caption || "");
      notify.success("Caption copied");
    } catch {
      notify.error("Could not copy. Select the text and copy it manually.");
    }
  };

  const markPosted = async () => {
    setSaving(true);
    setError("");
    try {
      const item = await api.markPosted(itemId, { Posted_URL: url.trim() || null });
      notify.success("Marked as posted");
      onPosted?.(item);
      onClose();
    } catch (err) {
      setError(errorMessage(err, "Could not mark as posted"));
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    if (!picked.length) {
      setError("Pick at least one connected account.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await api.publish(itemId, { Connection_Ids: picked, when });
      setJobs((prev) => [...(res?.jobs || []), ...prev]);
      const failed = (res?.jobs || []).filter((j) => j.Status === "Failed");
      if (when === "planned") notify.success("Scheduled. It will post automatically at the planned time.");
      else if (failed.length) notify.error(failed[0].Last_Error || "Publishing failed");
      else notify.success("Sent to publish");
      onPosted?.();
    } catch (err) {
      setError(errorMessage(err, "Could not publish"));
    } finally {
      setSaving(false);
    }
  };

  const toggle = (id) => setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <Modal
      title="Ready to post"
      onClose={onClose}
      wide
      footer={
        pack && !pack.posted ? (
          <>
            <button type="button" className="dmp-btn dmp-btn--ghost wk-btn-lg" onClick={onClose}>
              Close
            </button>
            <button type="button" className="dmp-btn wk-btn-lg" onClick={markPosted} disabled={saving}>
              <FaCheckCircle aria-hidden="true" /> Mark posted
            </button>
          </>
        ) : (
          <button type="button" className="dmp-btn wk-btn-lg" onClick={onClose}>
            Close
          </button>
        )
      }
    >
      {error ? <div className="dmp-error">{error}</div> : null}
      {!pack ? (
        <div className="wk-muted">Loading…</div>
      ) : (
        <>
          <div className="wk-detail-head">
            <strong style={{ fontSize: 16 }}>{pack.Title}</strong>
            {pack.posted ? <StatusPill status="Posted" /> : pack.approved ? <StatusPill status="Approved" /> : <StatusPill status="In Review" />}
          </div>
          {!pack.approved && !pack.posted ? (
            <div className="wk-banner">This is not approved yet. Send it for review before posting.</div>
          ) : null}

          <dl className="wk-kv">
            <dt>Where</dt>
            <dd>
              {pack.Platform || "—"}
              {pack.account ? (
                <>
                  {" · "}
                  {pack.account.Profile_URL || pack.account.Business_URL ? (
                    <a href={pack.account.Profile_URL || pack.account.Business_URL} target="_blank" rel="noreferrer">
                      {pack.account.Username || "Open page"} <FaExternalLinkAlt aria-hidden="true" size={11} />
                    </a>
                  ) : (
                    pack.account.Username
                  )}
                </>
              ) : null}
            </dd>
            <dt>When</dt>
            <dd>
              {pack.Planned_Post_Date ? fmtShortDate(pack.Planned_Post_Date) : "No date"}
              {pack.Planned_Post_Time ? ` at ${fmtTime(pack.Planned_Post_Time)}` : ""}
            </dd>
          </dl>

          <div className="wk-h3" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>1. Copy the caption</span>
            <button type="button" className="dmp-btn dmp-btn--ghost dmp-btn--sm" onClick={copyCaption} disabled={!pack.Caption}>
              <FaCopy aria-hidden="true" /> Copy
            </button>
          </div>
          <div className="wk-pack-caption">{pack.Caption || <span className="wk-muted">No caption yet</span>}</div>

          <div className="wk-h3">2. Download the media</div>
          {pack.media?.length ? (
            <div className="wk-media-grid">
              {pack.media.map((m, i) => (
                <div className="wk-media" key={m.URL || i}>
                  {m.Kind === "video" ? (
                    <video src={m.URL} controls preload="metadata" aria-label={m.Name || "Video"} />
                  ) : m.Kind === "image" ? (
                    <img src={m.URL} alt={m.Name || `Media ${i + 1}`} />
                  ) : null}
                  <a href={m.URL} target="_blank" rel="noreferrer" download>
                    <FaDownload aria-hidden="true" /> {m.Name || (m.Kind === "video" ? "Video" : "Open file")}
                  </a>
                </div>
              ))}
            </div>
          ) : (
            <div className="wk-muted">No media attached.</div>
          )}

          {!pack.posted ? (
            <>
              <div className="wk-h3">3. Post it, then paste the live link</div>
              <label className="wk-field">
                Live post link (optional)
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://instagram.com/p/…"
                />
              </label>
            </>
          ) : pack.Posted_URL ? (
            <p>
              Live:{" "}
              <a href={pack.Posted_URL} target="_blank" rel="noreferrer">
                {pack.Posted_URL}
              </a>
            </p>
          ) : null}

          {pack.connections?.length && !pack.posted ? (
            <>
              <div className="wk-h3">Or publish automatically</div>
              <div className="wk-chip-row" role="group" aria-label="Connected accounts">
                {pack.connections.map((c) => (
                  <button
                    key={c.Connection_Id}
                    type="button"
                    className="wk-chip"
                    aria-pressed={picked.includes(c.Connection_Id)}
                    onClick={() => toggle(c.Connection_Id)}
                  >
                    {c.Platform}: {c.Account_Name}
                  </button>
                ))}
              </div>
              <div className="wk-chip-row" role="group" aria-label="When to publish">
                <button type="button" className="wk-chip" aria-pressed={when === "now"} onClick={() => setWhen("now")}>
                  Now
                </button>
                <button
                  type="button"
                  className="wk-chip"
                  aria-pressed={when === "planned"}
                  onClick={() => setWhen("planned")}
                  disabled={!pack.Planned_Post_Date}
                >
                  At planned time
                </button>
                <button type="button" className="dmp-btn" onClick={publish} disabled={saving || !pack.approved}>
                  <FaPaperPlane aria-hidden="true" /> Publish
                </button>
              </div>
            </>
          ) : null}

          {jobs.length ? (
            <>
              <div className="wk-h3">Publishing history</div>
              <table className="wk-table">
                <thead>
                  <tr>
                    <th>Account</th>
                    <th>Status</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((j) => (
                    <tr key={j.Job_Id}>
                      <td>
                        {j.Platform}: {j.Account_Name}
                      </td>
                      <td>{j.Status}</td>
                      <td>
                        {j.Posted_URL ? (
                          <a href={j.Posted_URL} target="_blank" rel="noreferrer">
                            View post
                          </a>
                        ) : (
                          j.Last_Error || (j.Run_After ? `Runs ${j.Run_After.replace("T", " ").slice(0, 16)}` : "")
                        )}
                        {j.Status === "Failed" ? (
                          <button
                            type="button"
                            className="dmp-link"
                            style={{ marginLeft: 8 }}
                            onClick={async () => {
                              try {
                                const res = await api.retryJob(j.Job_Id);
                                setJobs((prev) => prev.map((x) => (x.Job_Id === j.Job_Id ? res : x)));
                              } catch (err) {
                                notify.error(errorMessage(err));
                              }
                            }}
                          >
                            Retry
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : null}
        </>
      )}
    </Modal>
  );
}

export default ReadyPack;
